<template>
	<div
		data-qa-multiselect
		:tabindex="searchable ? -1 : tabindex"
		:class="{
			'multiselect--active': isOpen,
			'multiselect--disabled': disabled,
			'multiselect--above': isAbove
		}"
		:state="state"
		class="multiselect"
		role="combobox"
		:aria-owns="'listbox-' + id"
		@focus="activate()"
		@blur="searchable ? false : deactivate()"
		@keydown.self.down.prevent="pointerForward()"
		@keydown.self.up.prevent="pointerBackward()"
		@keypress.enter.tab.stop.self="addPointerElement($event)"
		@keyup.esc="deactivate()"
	>
		<slot name="caret" :toggle="toggle">
			<div
				data-qa-multiselect-caret
				class="multiselect__select"
				@mousedown.prevent.stop="toggle()"
			/>
		</slot>
		<slot name="clear" :search="search" />
		<!-- type is used from flow (i.e. custom prop) -->
		<div ref="tags" class="multiselect__tags" :type="type">
			<slot
				name="selection"
				:search="search"
				:remove="removeElement"
				:values="visibleValues"
				:is-open="isOpen"
			>
				<div
					v-show="visibleValues.length > 0"
					v-if="!maxVisibleOptions && maxVisibleOptions !== 0"
					class="multiselect__tags-wrap"
				>
					<template v-for="(option, index) of visibleValues">
						<slot
							name="tag"
							:option="option"
							:search="search"
							:remove="removeElement"
							@mousedown.prevent
						>
							<span :key="index" class="multiselect__tag">
								<span v-text="getOptionLabel(option.title || option)" />
								<i
									tabindex="1"
									class="multiselect__tag-icon"
									@keypress.enter.prevent="removeElement(option)"
									@mousedown.prevent="removeElement(option)"
								/>
							</span>
						</slot>
					</template>
				</div>
				<div
					v-show="visibleValues.length > 0"
					v-else-if="maxVisibleOptions === 0"
					class="multiselect__tags-wrap"
				>
					<span></span>
				</div>
				<div v-show="visibleValues.length > 0" v-else class="multiselect__tags-wrap">
					<template v-for="(option, index) of visibleValues.slice(0, Number(maxVisibleOptions))">
						<slot
							name="tag"
							:option="option"
							:search="search"
							:remove="removeElement"
							@mousedown.prevent
						>
							<span :key="index" class="multiselect__tag">
								<span v-text="getOptionLabel(option.title || option)" />
								<i
									tabindex="1"
									class="multiselect__tag-icon"
									@keypress.enter.prevent="removeElement(option)"
									@mousedown.prevent="removeElement(option)"
								/>
							</span>
						</slot>
					</template>
				</div>
				<template v-if="internalValue && internalValue.length > limit">
					<slot name="limit">
						<strong class="multiselect__strong" v-text="limitText(internalValue.length - limit)" />
					</slot>
				</template>
			</slot>
			<transition name="multiselect__loading">
				<slot name="loading">
					<div v-show="loading" class="multiselect__spinner" />
				</slot>
			</transition>
			<input
				v-if="searchable"
				:id="id"
				ref="search"
				data-qa-multiselect-input
				:name="name"
				type="text"
				autocomplete="off"
				spellcheck="false"
				:placeholder="placeholder"
				:style="inputStyle"
				:value="search"
				:disabled="disabled"
				:tabindex="tabindex"
				class="multiselect__input"
				:aria-controls="'listbox-' + id"
				@input="updateSearch($event.target.value)"
				@focus.prevent="activate()"
				@blur.prevent="deactivate()"
				@keyup.esc="deactivate()"
				@keydown.down.prevent="pointerForward()"
				@keydown.up.prevent="pointerBackward()"
				@keypress.enter.prevent.stop.self="addPointerElement($event)"
				@keydown.delete.stop="removeLastElement()"
			/>
			<span v-if="isSingleLabelVisible" class="multiselect__single" @mousedown.prevent="toggle">
				<slot name="singleLabel" :option="singleValue">
					<template>{{ currentOptionLabel }}</template>
				</slot>
			</span>
			<span
				v-if="isPlaceholderVisible"
				class="multiselect__placeholder"
				@mousedown.prevent="toggle"
			>
				<slot name="placeholder">
					{{ placeholder }}
				</slot>
			</span>
		</div>
		<transition name="multiselect">
			<div
				v-show="isOpen"
				ref="list"
				class="multiselect__content-wrapper flow-add-scrollbar"
				tabindex="-1"
				:style="{
					maxHeight: optimizedHeight + 'px',
					width: optionsWidth,
					top: optionsTop
				}"
				@focus="activate"
				@mousedown.prevent
			>
				<ul :id="'listbox-' + id" class="multiselect__content" :style="contentStyle" role="listbox">
					<slot name="beforeList" />
					<li v-if="multiple && max === internalValue.length">
						<span class="multiselect__option">
							<slot name="maxElements">
								Maximum of {{ max }} options selected. First remove a selected option to select
								another.
							</slot>
						</span>
					</li>
					<template v-if="!max || internalValue.length < max">
						<li
							v-for="(option, index) of filteredOptions"
							:id="id + '-' + index"
							:key="index"
							:data-header="option.header && !headerInOption ? option.header : null"
							class="multiselect__element"
							:class="{
								'last-child': index + 1 === filteredOptions.length,
								'border-bottom': option.bottomBorder
							}"
							:role="!(option && (option.$isLabel || option.$isDisabled)) ? 'option' : null"
						>
							<span
								v-if="!(option && (option.$isLabel || option.$isDisabled))"
								:class="optionHighlight(index, option)"
								:data-select="option && option.isTag ? tagPlaceholder : selectLabelText"
								:data-selected="selectedLabelText"
								:data-deselect="deselectLabelText"
								class="multiselect__option"
								:data-qa-multiselect-option="getOptionLabel(option)"
								@click.stop="select(option)"
								@mouseenter.self="pointerSet(index)"
							>
								<slot name="option" :option="option" :search="search" :index="index">
									<span>{{ getOptionLabel(option) }}</span>
								</slot>
								<Icon
									v-if="isSelected(option) && showOptionSelector"
									name="i-tick"
									state="dark"
									:effects="false"
									size="small"
								/>
							</span>
							<span
								v-if="option && (option.$isLabel || option.$isDisabled)"
								:data-select="groupSelect && selectGroupLabelText"
								:data-deselect="groupSelect && deselectGroupLabelText"
								:class="groupHighlight(index, option)"
								class="multiselect__option"
								@mouseenter.self="groupSelect && pointerSet(index)"
								@mousedown.prevent="selectGroup(option)"
							>
								<slot name="option" :option="option" :search="search" :index="index">
									<span>{{ getOptionLabel(option) }}</span>
								</slot>
								<Icon
									v-if="isSelected(option) && showOptionSelector"
									name="i-tick"
									state="dark"
									:effects="false"
									size="small"
								/>
							</span>
						</li>
					</template>
					<li v-show="showNoResults && filteredOptions.length === 0 && search && !loading">
						<span
							class="multiselect__option text-wrap paragraph-1"
							data-qa-multiselect-empty-search
						>
							<slot name="noResult" :search="search">
								No elements found. Consider changing the search query.
							</slot>
						</span>
					</li>
					<li v-show="showNoOptions && options.length === 0 && !search && !loading">
						<span class="multiselect__option" data-qa-multiselect-empty>
							<slot name="noOptions">List is empty.</slot>
						</span>
					</li>
					<slot name="afterList" />
				</ul>
			</div>
		</transition>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Multiselect from "vue-multiselect";
import Icon from "./../Icon/Icon";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default defineComponent({
	name: "Multiselect",
	components: { Icon },
	mixins: [Multiselect],
	props: {
		/**
		 * 'default' | 'error' | 'success' | 'warning'
		 */
		state: {
			type: String,
			default: "default"
		},
		type: {
			type: String,
			default: "curved",
			validator(value) {
				return ["curved", "rounded"].includes(value);
			}
		},
		headerInOption: {
			type: Boolean,
			default: false
		},
		maxVisibleOptions: {
			type: [Number, String],
			default: ""
		},
		showOptionSelector: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			optionsWidth: "50px",
			optionsTop: "0px"
		};
	},
	mounted() {
		window.addEventListener("scroll", this.handleWindowScroll, {
			capture: true
		});
		this.updateDimentions();
	},
	beforeDestroy() {
		window.removeEventListener("scroll", this.handleWindowScroll, {
			capture: true
		});
	},
	updated() {
		this.updateDimentions();
	},
	methods: {
		updateDimentions() {
			this.optionsWidth = `${this.$el.offsetWidth}px`;

			const spaceAbove = this.$el.getBoundingClientRect().top;
			const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
			const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;

			const optionsHeight = this.$refs.list.offsetHeight;
			const heightToApply = Math.min(optionsHeight, this.maxHeight);
			if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
				this.preferredOpenDirection = "below";
				this.optimizedHeight = +Math.min(spaceBelow - 40, this.maxHeight).toFixed(0);
				this.optionsTop = `${(this.$el.getBoundingClientRect().top + this.$el.offsetHeight).toFixed(
					0
				)}px`;
			} else {
				this.preferredOpenDirection = "above";
				this.optimizedHeight = +Math.min(spaceAbove - 40, this.maxHeight).toFixed(0);

				this.optionsTop = `${(this.$el.getBoundingClientRect().top - heightToApply).toFixed(0)}px`;
			}
		},
		handleWindowScroll() {
			if (this.isOpen) {
				this.updateDimentions();
			}
		}
	}
});
</script>
<style lang="scss">
@mixin multiselect(
	$font-light,
	$element-light,
	$element,
	$font,
	$element-dark,
	$primary,
	$placeholder
) {
	div.multiselect {
		> div.multiselect__tags {
			background-color: $element-light;

			> div.multiselect__spinner {
				background: $element-light;
				&:before {
					border-top-color: $primary;
				}
				&:after {
					border-top-color: $primary;
				}
			}

			> div.multiselect__tags-wrap {
				> span.multiselect__tag {
					background: var(--gray-500);
					color: $font;

					> .multiselect__tag-icon {
						&:after {
							color: $font;
						}
						&:hover {
							background: var(--gray-600);

							&:after {
								color: $primary;
							}
						}
					}
				}
			}

			> span.multiselect__single,
			> input.multiselect__input {
				background-color: $element-light;
				color: $font;

				&::placeholder {
					color: $placeholder;
				}
			}

			> span.multiselect__placeholder {
				color: $placeholder;
				width: 100%;
				background: transparent;

				// > div.flow-select-loading-placeholder {
				// 	display: flex;
				// 	justify-content: space-between;
				// 	width: 100%;

				// 	> .icon-container[name="loading"] > .icon {
				// 		animation: spin 2s linear infinite !important;
				// 	}
				// }
				// background-color: $element-light;
			}
		}

		> div.multiselect__select {
			&:before {
				border-color: $font transparent transparent;
				color: $font;
			}
		}

		&:before {
			content: none;
		}
	}

	div.multiselect {
		> div.multiselect__content-wrapper {
			border: 1px solid $element;
			color: $font;

			background-color: $element-light;
			background: $element-light;

			> ul.multiselect__content {
				> li.multiselect__element {
					&.border-bottom {
						border-bottom: 1px solid $element;
					}
					&[data-header] {
						position: relative;
						margin-top: 36px;
						&:before {
							content: attr(data-header);
							position: absolute;
							bottom: 100%;
							height: 36px;
							font-size: 12px;
							font-family: var(--body-font);
							line-height: 12px;

							display: flex;
							align-items: center;
							padding: 16px;
							box-sizing: border-box;
							color: var(--dashboard-font-light);
						}
					}
					> span.multiselect__option {
						&--highlight {
							background: $element;

							color: $font;
							&:after {
								content: attr(data-select);
								background: $element;
								color: $font;
							}
						}
						&--selected {
							background: transparent;
							color: $font;

							&:hover {
								background: $element;
								color: $font;
							}

							&:after {
								content: "";
								opacity: 0;
								// padding: 0px;
								// // width: 16px;
								// height: 100%;
								// border-right: 4px solid $primary;
								// border-top-left-radius: 2px;
								// border-bottom-left-radius: 2px;
							}
						}
						&--disabled {
							background: $element-dark !important;
							color: $font;
							opacity: 0.5;
						}

						> span {
							word-break: break-all;
							max-width: 100%;
							white-space: pre-wrap;
							line-height: 18px;
						}
					}
				}
			}
		}
	}
}

div.multiselect {
	// max-height: 36px;
	min-height: 36px;
	min-width: 100px;
	z-index: auto;

	&--disabled {
		background: inherit;
		cursor: not-allowed;
		opacity: 0.6;
		pointer-events: auto;
		.multiselect__select {
			background: inherit;
			color: inherit;
			cursor: not-allowed;
		}
	}

	> div.multiselect__tags {
		padding: 0px 36px 0 12px;
		min-height: 36px;
		border: none;
		&[type="curved"] {
			border-radius: 4px;
			> div.multiselect__tags-wrap > span.multiselect__tag {
				border-radius: 4px;
			}
		}

		&[type="rounded"] {
			border-radius: 18px;
			> div.multiselect__tags-wrap > span.multiselect__tag {
				border-radius: 11px;
			}
		}

		> div.multiselect__tags-wrap {
			display: inline-flex;
			align-items: center;
			min-height: 36px;
			flex-wrap: wrap;
			padding-top: 4px;

			> span.multiselect__tag {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				> span {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					max-width: 200px;
					display: inline-block;
				}
			}
		}

		> div.multiselect__spinner {
			border-radius: 18px;
			top: 0px;

			&:before {
				top: 47%;
			}

			&:after {
				top: 47%;
			}
		}

		> span.multiselect__single,
		> input.multiselect__input {
			margin-bottom: 0px;
			min-height: 36px;
			display: inline-flex;
			font-size: 12px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			top: 0px;
			padding: 0px;
			line-height: 14px;
			align-items: center;
			border: none;

			&::placeholder {
				font-size: 12px;
			}
		}

		> span.multiselect__placeholder {
			display: inline-flex;
			margin-bottom: 0px;
			height: 36px;
			align-items: center;
			padding: 0px 0px 0px 0px;
			font-size: 12px;
			border: none;
		}
	}

	> div.multiselect__select {
		height: 36px;
		width: 36px;
		padding: 0px 0px;
		// top: 5px;

		&:before {
			position: relative;
			right: 0;
			top: 65%;
			margin-top: 4px;
			border-style: solid;
			border-width: 5px 5px 0;
			content: "";
		}
	}

	&[state="error"] {
		div.multiselect__tags {
			box-shadow: 0 0 0 1px var(--dashboard-danger);
		}
	}
}

div.multiselect {
	> div.multiselect__content-wrapper {
		margin-top: 2px;
		margin-bottom: 2px;
		padding-bottom: 0px;
		-moz-box-shadow: 0 0 4px rgba(0, 0, 0, 0.7);
		-webkit-box-shadow: 0 0 4px rgba(0, 0, 0, 0.7);
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.7);
		position: fixed;
		height: fit-content;

		> ul.multiselect__content {
			max-width: 100%;
			> li.multiselect__element {
				padding: 0px 0px 0px 0px;
				> span.multiselect__option {
					min-height: 36px;
					padding: 0px 8px 0px 8px;
					display: flex;
					align-items: center;
					font-size: 12px;
					border-radius: 0px;
					justify-content: space-between;

					&--highlight {
						outline: none;

						&:after {
							content: attr(data-select);

							min-height: 36px;
							height: 100%;
						}
					}
					&--selected {
						font-weight: normal;
						&:hover {
							outline: none;
						}
					}
					&--disabled {
						cursor: not-allowed;
					}

					> div.option-column-flex {
						display: flex;
						flex-direction: column;
						padding: 4px 0px 4px 0px;
						> * {
							white-space: normal;
							word-break: break-all;
						}
					}
				}
			}
			> li.multiselect__element:not(.last-child) {
				margin-bottom: 2px;
			}
			> li.multiselect__element.last-child {
				margin-bottom: 0px;
			}
		}
	}
}

@include multiselect(
	var(--dashboard-font-light),
	var(--dashboard-input),
	var(--gray-800),
	var(--dashboard-font),
	var(--dashboard-element-dark),
	var(--dashboard-primary),
	var(--dashboard-input-placeholder)
);
.sideout-container {
	@include multiselect(
		var(--slideout-font-light),
		var(--slideout-input),
		var(--gray-800),
		var(--slideout-font),
		var(--slideout-element-dark),
		var(--slideout-primary),
		var(--slideout-input-placeholder)
	);
}
</style>
